import { useMemo, useState } from "react";
import { Button, Form } from "antd";
import dayjs from "dayjs";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { CloseOutlined } from "@ant-design/icons";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import CustomSwitch from "../../../global/components/inputWidgets/CustomSwitch";
import {
  getCodeKey,
  LicenseCodeSelectTable,
} from "../../../global/components/LicenseCodeSelectTable";
import usePost from "../../../global/hooks/usePost";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { itemOptions, resolveItemValue } from "../../../utils/Common";
import {
  calcEndTime,
  formatDateTime,
  isLifetimeTerm,
} from "./PurchaseLicenseItemForm";

const getItemValue = (record) => {
  const n = Number(record?.Item);
  return Number.isFinite(n) ? n : null;
};

const getItemTypeLabel = (record) =>
  record?.ItemName ||
  (record?.Item != null ? `Item ${record.Item}` : "this item");

const getColumns = ({ form, selectedRowKeys, showRenewal }) => {
  const columns = [
    {
      title: "Start Time",
      key: "StartTime",
      width: 150,
      render: (_, record) => {
        const key = getCodeKey(record);
        const isSelected = selectedRowKeys.includes(key);
        return (
          <div className="prepurchased-row-control">
            <CustomDateTimePicker
              name={["edits", key, "StartTime"]}
              defaultValue={dayjs()}
              format="YYYY-MM-DD HH:mm"
              size="small"
              allowClear={false}
              disablePast
              rules={
                isSelected
                  ? [{ required: true, message: "Start Time is required" }]
                  : []
              }
              onChange={(value) =>
                form.setFieldValue(
                  ["edits", key, "EndTime"],
                  calcEndTime(value, record?.Term),
                )
              }
            />
          </div>
        );
      },
    },
    {
      title: "End Time",
      key: "EndTime",
      width: 150,
      render: (_, record) =>
        isLifetimeTerm(record?.Term) ? (
          ""
        ) : (
          <div className="prepurchased-row-control">
            <CustomDateTimePicker
              name={["edits", getCodeKey(record), "EndTime"]}
              defaultValue={calcEndTime(dayjs(), record?.Term)}
              format="YYYY-MM-DD HH:mm"
              size="small"
              disabled
            />
          </div>
        ),
    },
  ];

  if (showRenewal) {
    columns.push({
      title: "Renewal",
      key: "Renewal",
      width: 80,
      align: "center",
      render: (_, record) =>
        isLifetimeTerm(record?.Term) ? (
          ""
        ) : (
          <div className="prepurchased-row-control flex justify-center">
            <CustomSwitch
              name={["edits", getCodeKey(record), "Renewal"]}
              defaultChecked={false}
              size="small"
            />
          </div>
        ),
    });
  }

  return columns;
};

export const PurchaseLicenseItemPicker = ({
  purchaseId,
  deviceNo,
  existingItems = [],
  showRenewal = false,
  onCancel,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [selectedRows, setSelectedRows] = useState([]);
  const selectedRowKeys = selectedRows.map((row) => row.key);
  const [submitting, setSubmitting] = useState(false);
  const postUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${purchaseId}/items/`;
  const { postData: postAction } = usePost(postUrl);

  const usedItemValues = useMemo(() => {
    const used = new Set();
    existingItems.forEach((row) => {
      const value = resolveItemValue(row, itemOptions);
      if (value != null) used.add(value);
    });
    return used;
  }, [existingItems]);

  const isSelected = (record) => selectedRowKeys.includes(getCodeKey(record));

  // One code per item on a purchase.
  const getDisabledReason = (record) => {
    const itemValue = getItemValue(record);
    if (usedItemValues.has(itemValue)) {
      return `${getItemTypeLabel(record)} is already added to this purchase.`;
    }
    const otherSelected = selectedRows.some(
      (row) =>
        row.key !== getCodeKey(record) && getItemValue(row) === itemValue,
    );
    if (otherSelected) {
      return `Only one ${getItemTypeLabel(record)} can be selected. Unselect the selected ${getItemTypeLabel(record)} first.`;
    }
    return null;
  };

  const onToggle = (record) =>
    setSelectedRows((prev) =>
      prev.some((row) => row.key === record.key)
        ? prev.filter((row) => row.key !== record.key)
        : [...prev, record],
    );

  const buildPayload = (row) => {
    const edit = form.getFieldValue(["edits", row.key]) || {};
    const isLifetime = isLifetimeTerm(row?.Term);
    const start = edit.StartTime || dayjs();
    return {
      CodeNo: Number(row.CodeNo),
      StartTime: formatDateTime(start),
      EndTime: formatDateTime(
        isLifetime ? null : calcEndTime(start, row?.Term),
      ),
      Renewal: !isLifetime && showRenewal && edit.Renewal ? 1 : 0,
    };
  };

  const onSubmit = async () => {
    if (!selectedRows.length) {
      openErrorNotification("Please select at least one item.");
      return;
    }
    if (deviceNo == null || deviceNo === "") {
      openErrorNotification("Site is not available.");
      return;
    }
    try {
      await form.validateFields(
        selectedRows.map((row) => ["edits", row.key, "StartTime"]),
      );
    } catch {
      return;
    }

    setSubmitting(true);
    const failedRows = [];
    for (const row of selectedRows) {
      const result = await postAction(buildPayload(row));
      if (result == null) failedRows.push(row);
    }
    setSubmitting(false);

    if (failedRows.length < selectedRows.length) onSaved?.();
    if (!failedRows.length) {
      openConfirmNotification("Success");
      onCancel?.();
      return;
    }
    setSelectedRows(failedRows);
    openErrorNotification(
      failedRows.length === selectedRows.length
        ? "Something went wrong"
        : `${selectedRows.length - failedRows.length} added, ${failedRows.length} failed.`,
    );
  };

  return (
    <>
      <Form form={form} layout="vertical" className="prepurchased-items-form">
        <LicenseCodeSelectTable
          columns={getColumns({ form, selectedRowKeys, showRenewal })}
          isSelected={isSelected}
          getDisabledReason={getDisabledReason}
          onToggle={onToggle}
        />
      </Form>

      <div className="flex justify-center mt-4 gap-4">
        <Button
          type="primary"
          className="mr-2 w-[104px] h-[30px] font-medium"
          loading={submitting}
          disabled={submitting}
          onClick={onSubmit}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          Apply
        </Button>
        <Button
          onClick={onCancel}
          disabled={submitting}
          className="reset-button w-[104px] h-[30px] font-medium"
        >
          <CloseOutlined className="font-medium" />
          Close
        </Button>
      </div>
    </>
  );
};
